import React from "react";
import { useTranslation } from "react-i18next";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";

const RefundPolicy = () => {
  const { t } = useTranslation();

  return (
      <div className="flex flex-col mt-28 mb-8 py-6 px-4 sm:px-8 max-w-4xl mx-auto bg-white dark:bg-neutral-900/40 shadow-sm border border-gray-100 dark:border-white/5 rounded-2xl max-h-[85vh]">
        {/* Header Section */}
        <div className="flex flex-col items-center text-center gap-3 pt-4 pb-6 shrink-0">
          <div className="p-3 bg-blue-50 dark:bg-blue-500/10 rounded-xl text-blue-500 dark:text-dark-primary">
            <PaymentsOutlinedIcon className="w-7 h-7" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
            MoonSkins.gg – REFUND POLICY
          </h1>
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            Last Updated: Feb 11, 2025
          </p>
        </div>

        {/* Content Area with Overflow Scroll */}
        <div className="flex-1 overflow-y-auto pr-2 space-y-6 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 dark:scrollbar-thumb-neutral-800 text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-4">
          <section className="p-5 bg-blue-50/30 dark:bg-blue-500/5 border border-blue-100/50 dark:border-blue-500/10 rounded-xl">
            <p>
              This Refund Policy outlines the terms and conditions regarding refunds for purchases made on the MoonSkins marketplace. Please read this policy carefully before making a purchase.
            </p>
          </section>

          <section className="p-5 bg-gray-50/50 dark:bg-neutral-800/20 border border-gray-100 dark:border-white/5 rounded-xl space-y-2">
            <h2 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
              ORDER CANCELLATION AND REFUNDS (PRE-DELIVERY)
            </h2>
            <p><strong>Right to Cancel:</strong> You have the right to cancel your order and request a full refund before the seller has delivered the purchased item to your Steam account.</p>
            <p><strong>How to Cancel:</strong> To cancel a purchase, navigate to your “trades” page and click the “Cancel” button (you need to wait at least 30 minutes after purchase to cancel). You should immediately receive the funds back to your MoonSkins account balance.</p>
            <p><strong>Refund Process:</strong> If you cancel your order before delivery, you can ask our support for a full refund of your deposit back to your payment method. The refund will be processed to the original payment method used for the deposit. Please allow a reasonable processing time (typically 5-10 business days). Support may not issue partial refunds if only a partial deposit was spent.</p>
          </section>

          <section className="p-5 bg-gray-50/50 dark:bg-neutral-800/20 border border-gray-100 dark:border-white/5 rounded-xl space-y-2">
            <h2 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
              FINAL SALE (POST-DELIVERY)
            </h2>
            <p><strong>Irreversible Trades:</strong> Once the seller has successfully delivered the purchased item to your Steam™ account and you have accepted the trade on Steam™, the transaction is considered final and irreversible.</p>
            <p><strong>No Refunds After Delivery:</strong> Due to the nature of digital item transfers, we cannot offer refunds after delivery. Trades cannot be reversed by MoonSkins on Steam™.</p>
            <p><strong>Verification Before Acceptance:</strong> Carefully inspect the trade offer on Steam™ before accepting it. MoonSkins uses automated measures to ensure you receive the correct item, but we recommend checking it yourself.</p>
          </section>

          <section className="p-5 bg-gray-50/50 dark:bg-neutral-800/20 border border-gray-100 dark:border-white/5 rounded-xl">
            <h2 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
              RESELLING ITEMS
            </h2>
            <p><strong>Resale Option:</strong> If you purchased an item by mistake or no longer wish to keep it, you may list it for sale again on MoonSkins marketplace, subject to standard listing guidelines and fees. If the item hasn’t been delivered yet, you may cancel your purchase via the website.</p>
          </section>

          <section className="p-5 bg-gray-50/50 dark:bg-neutral-800/20 border border-gray-100 dark:border-white/5 rounded-xl space-y-2">
            <h2 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
              DISPUTES AND ISSUES (NOT RELATED TO REFUNDS AFTER DELIVERY)
            </h2>
            <p>If the seller doesn’t deliver the item in a timely manner, you can cancel your purchase as outlined above.</p>
            <p>If you receive a different item than purchased, contact support. Typically you will be automatically refunded in scenarios like this without contacting support.</p>
          </section>

          <section className="p-5 bg-gray-50/50 dark:bg-neutral-800/20 border border-gray-100 dark:border-white/5 rounded-xl space-y-2">
            <h2 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
              CHARGEBACKS
            </h2>
            <p><strong>Contact Support First:</strong> Before initiating a chargeback, you agree to first contact MoonSkins support to attempt to resolve the issue.</p>
            <p><strong>Unauthorized Chargebacks:</strong> Initiating a chargeback without contacting support first may be considered unauthorized.</p>
            <p><strong>Consequences:</strong> Unauthorized chargebacks can result in account suspension or termination. You will be responsible for fees incurred by MoonSkins due to unauthorized chargebacks.</p>
            <p><strong>Legitimate Chargebacks:</strong> You can initiate a chargeback if you have a legitimate, unresolved dispute and have first contacted support as outlined in this policy.</p>
          </section>

          <section className="p-5 bg-gray-50/50 dark:bg-neutral-800/20 border border-gray-100 dark:border-white/5 rounded-xl">
            <h2 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
              CONTACTING SUPPORT
            </h2>
            <p>For questions regarding this Refund Policy or to report issues, contact MoonSkins support via MoonSkins.gg Live Chat. Provide your order number and a detailed description of your request.</p>
          </section>

          <section className="p-5 bg-gray-50/50 dark:bg-neutral-800/20 border border-gray-100 dark:border-white/5 rounded-xl">
            <h2 className="text-base font-bold text-gray-900 dark:text-white mb-2 uppercase tracking-wide">
              POLICY UPDATES
            </h2>
            <p>MoonSkins reserves the right to modify this Refund Policy at any time. Changes will be posted on our website and apply to purchases made after the update. Review this policy periodically.</p>
          </section>
        </div>
      </div>
  );
};

export default RefundPolicy;